import { Geolocation, Position } from "@capacitor/geolocation";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import React from "react";
import { MapRef } from "react-map-gl/maplibre";
import { useCallbackRef } from "../hooks/useCallbackRef";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import BaseMap from "./BaseMap";
import { AppAbstractModal } from "./Generic/AppModal/AppAbstractModal";
import Spinner from "./Generic/Spinner";
import CaregiverMapMarker from "./MainV2Screen/components/CaregiverMapMarker";

const TIMEOUT = 10000;

type Props = {
  isOpen: boolean;
  onTimeout: () => void;
  onError: (error: GeolocationPositionError) => void;
  onSuccess?: (position: Position) => void;
  onRequestClose: () => void;
};

function useWatchLocation(params: {
  enabled: boolean;
  onError: (error: GeolocationPositionError) => void;
  onSuccess?: (position: Position) => void;
}) {
  const onErrorRef = useCallbackRef(params.onError);
  const onSuccessRef = useCallbackRef(params.onSuccess);

  const [location, setLocation] = React.useState<Position | null>(null);

  React.useEffect(() => {
    if (!params.enabled) {
      return;
    }

    const watchId = Geolocation.watchPosition(
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      },
      (position, error) => {
        if (position !== null) {
          setLocation(position);
          onSuccessRef(position);
        }

        if (error) {
          onErrorRef(error);
        }
      }
    );

    return () => {
      watchId.then((id) => Geolocation.clearWatch({ id }));
    };
  }, [onErrorRef, onSuccessRef, params.enabled]);

  return location;
}

export default function WatchLocationModal(props: Props) {
  const location = useWatchLocation({
    onError: props.onError,
    onSuccess: props.onSuccess,
    enabled: props.isOpen,
  });
  const mapRef = React.useRef<MapRef>(null);
  const isInitialFlyTo = React.useRef(true);
  const [isMapLoaded, setIsMapLoaded] = React.useState(false);
  const onTimeoutRef = useCallbackRef(props.onTimeout);

  React.useEffect(() => {
    if (!isMapLoaded || !props.isOpen) {
      return;
    }

    const timeout = setTimeout(() => {
      onTimeoutRef();
    }, TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
  }, [isMapLoaded, onTimeoutRef, props.isOpen]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (!mapRef.current || !props.isOpen) {
        return;
      }

      const map = mapRef.current.getMap();

      if (isInitialFlyTo.current) {
        map.flyTo({
          center: [location?.coords.longitude ?? 0, location?.coords.latitude ?? 0],
          zoom: 12,
          animate: false,
        });

        isInitialFlyTo.current = false;
      }

      const newZoom = isMapLoaded ? Math.min(map.getZoom() + 1, 16) : map.getZoom();

      map.flyTo({
        center: [location?.coords.longitude ?? 0, location?.coords.latitude ?? 0],
        zoom: newZoom,
        animate: true,
        duration: 2000,
      });
    }, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [isMapLoaded, location?.coords.latitude, location?.coords.longitude, props.isOpen]);

  const coordinates = {
    latitude: location?.coords.latitude ?? 0,
    longitude: location?.coords.longitude ?? 0,
  };

  return (
    <Modal isOpen={props.isOpen} onRequestClose={props.onRequestClose}>
      <Root>
        <MapWrapper data-loaded={isMapLoaded}>
          <BaseMap
            ref={mapRef}
            destination={{ lat: coordinates.latitude, lng: coordinates.longitude }}
            style={{ height: "100%" }}
            onLoad={() => setIsMapLoaded(true)}
            dragPan={false}
            dragRotate={false}
            touchPitch={false}
            touchZoomRotate={false}
            scrollZoom={false}
          >
            {isMapLoaded && (
              <CaregiverMapMarker
                caregiver={{
                  addressGeoLocation: { lat: coordinates.latitude, lng: coordinates.longitude },
                }}
                isActive={false}
                onClick={() => {}}
              />
            )}
          </BaseMap>
        </MapWrapper>
        <ProgressBar data-animate={isMapLoaded} />
        <Header>
          <Heading>{translate(STRINGS.PLEASE_WAIT_WHILE_WERE_VALIDATING_YOUR_LOCATION)}</Heading>
          <Message>
            {translate(STRINGS.THIS_MAY_TAKE_UP_TO_X_SECONDS).replace(
              "{SECONDS}",
              `${TIMEOUT / 1000}`
            )}
          </Message>
          <SpinnerWrapper>
            <Spinner size={36} color="var(--primary-color)" />
          </SpinnerWrapper>
        </Header>
      </Root>
    </Modal>
  );
}

const Modal = styled(AppAbstractModal)`
  > .container {
    background-color: white;
    border-radius: 16px;
    overflow: hidden;
  }
`;

const Root = styled.div`
  height: 100%;
`;

const MapWrapper = styled.div`
  background-color: var(--tw-slate-100);
  height: 254px;

  > div {
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }

  &[data-loaded="true"] > div {
    opacity: 1;
  }
`;

const progressBarKeyframes = keyframes`
  0% {
    transform: scaleX(0);
  }
  100% {
    transform: scaleX(1);
  }
`;

const ProgressBar = styled.div<{ ["data-animate"]: boolean }>`
  height: 4px;
  width: 100%;
  background-color: var(--primary-color);
  transform-origin: left;
  transform: scaleX(0);

  &[data-animate="true"] {
    animation: ${progressBarKeyframes} ${TIMEOUT}ms linear forwards;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 32px 16px;
`;

const Heading = styled.h2`
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1;
  margin: 0;
  text-align: center;
`;

const Message = styled.p`
  color: var(--tw-slate-500);
  font-size: 1rem;
  margin: 0;
  text-align: center;
  padding: 0 16px;
  line-height: 1;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
`;
